import React, { useMemo, useState } from "react";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormInput from "Common/Custom/FormInput";
import { Modal, Button } from "react-bootstrap";


const getRate = (room, fieldName) => {
    const chargeMap = {
        extra_bed: "extra_bed_charge",
        extra_person: "extra_person_charge",
        extra_child: "extra_child_charge",
    };

    return Number(room[chargeMap[fieldName]] || 0);
};

const calculateNights = (from, to) => {
    if (!from || !to) return 1;

    return Math.max(
        1,
        Math.ceil((new Date(to) - new Date(from)) / 86400000)
    );
};

const ExtraChargeSummary = ({
    room,
    values,
    extraFields = [],
}) => {
    const from =
        room.extracheck_in_date || values.checkin_date;

    const to =
        room.extracheck_out_date || values.checkout_date;

    const nights = useMemo(
        () => calculateNights(from, to),
        [from, to]
    );

    const qtyFields = extraFields.filter(
        (field) =>
            field.type === "number" &&
            !field.disabled
    );

    const rows = qtyFields
        .map((field) => ({
            label: field.label?.trim(),
            qty: Number(room[field.name] || 0),
            rate: getRate(room, field.name),
            chargeName: field.name,
        }))
        .filter((row) => row.qty > 0);

    const grandTotal = rows.reduce(
        (sum, row) =>
            sum + row.qty * row.rate * nights,
        0
    );

    if (!rows.length) return null;

    return (
        <div
            className="mt-3"
            style={{
                background: "var(--bs-body-bg)",
                border:
                    "0.5px solid var(--bs-border-color)",
                borderRadius: 10,
                overflow: "hidden",
            }}
        >
            <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-light border-bottom">
                <span className="fw-semibold small d-flex align-items-center gap-1">
                    <i
                        className="ti ti-calculator"
                        style={{ fontSize: 15 }}
                    />
                    Extra Facility Summary
                </span>

                <span
                    className="badge fw-normal"
                    style={{
                        background: "#E6F1FB",
                        color: "#185FA5",
                        borderRadius: 20,
                        fontSize: 11,
                    }}
                >
                    {nights} night
                    {nights !== 1 ? "s" : ""}
                </span>
            </div>

            <table
                className="table table-sm table-borderless table-hover mb-0"
                style={{ tableLayout: "fixed" }}
            >
                <thead className="table-light">
                    <tr style={{ fontSize: 11 }}>
                        <th style={{ width: "36%" }}>
                            Item
                        </th>
                        <th
                            className="text-center"
                            style={{ width: "14%" }}
                        >
                            Qty
                        </th>
                        <th
                            className="text-center"
                            style={{ width: "18%" }}
                        >
                            Rate
                        </th>
                        <th
                            className="text-center"
                            style={{ width: "14%" }}
                        >
                            Nights
                        </th>
                        <th
                            className="text-end"
                            style={{ width: "18%" }}
                        >
                            Amount
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={row.chargeName}
                            style={{ fontSize: 13 }}
                        >
                            <td>{row.label}</td>

                            <td className="text-center">
                                {row.qty}
                            </td>

                            <td className="text-center">
                                ₹
                                {row.rate.toLocaleString()}
                            </td>

                            <td className="text-center">
                                {nights}
                            </td>

                            <td className="text-end fw-semibold text-success">
                                ₹
                                {(
                                    row.qty *
                                    row.rate *
                                    nights
                                ).toLocaleString()}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div
                className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-top"
                style={{
                    borderTop:
                        "0.5px solid var(--bs-border-color)",
                }}
            >
                <span className="text-muted small">
                    Total Extra Charges
                </span>

                <span
                    className="fw-semibold text-success"
                    style={{ fontSize: 15 }}
                >
                    ₹{grandTotal.toLocaleString()}
                </span>
            </div>
        </div>
    );
};

const ExtraFacilitySection = ({
    extraFields = [],
    room,
    values,
    errors,
    index,
    onRoomFieldEvent,
    ChildrenMinAge,
    ChildrenMaxAge,
}) => {
    const [showBill, setShowBill] = useState(false);

    const qtyFields = extraFields.filter(
        (field) => field.type === "number" && !field.disabled
    );

    const exbedCapabilityLimit =
        Number(room.extra_Capablity) === 1
            ? Number(room.exbed_capability_limit || 0)
            : 0;

    const hasQty = qtyFields.some(
        (field) => Number(room[field.name] || 0) > 0
    );

    const totalItems = qtyFields.reduce(
        (sum, field) => sum + Number(room[field.name] || 0),
        0
    );

    const estimatedTotal = useMemo(() => {
        const nights = calculateNights(
            room.extracheck_in_date || values.checkin_date,
            room.extracheck_out_date || values.checkout_date
        );

        return qtyFields.reduce((sum, field) => {
            const qty = Number(room[field.name] || 0);
            const rate = getRate(room, field.name);
            return sum + qty * rate * nights;
        }, 0);
    }, [room, values, qtyFields]);

    return (
        <td colSpan={4} className="p-3 border-bottom-0">
            <div className="row g-2">
                {extraFields.map((field) => {
                    if (
                        field.type !== "date"
                    )
                        return null;

                    return (
                        <FormDatePicker
                            key={field.name}
                            field={{
                                ...field,
                                disabled:
                                    !room.extra_Capablity ||
                                    !values.checkout_date,
                                minDate:
                                    values.checkin_date,
                                maxDate:
                                    values.checkout_date,
                            }}
                            value={room[field.name] ? room[field.name] : room.extra_Capablity ? ((room[field.name] === "extracheck_in_date") ? values.checkin_date : values.checkout_date) : ""}
                            handleChange={onRoomFieldEvent(
                                index
                            )}
                            error={
                                errors?.rooms?.[
                                index
                                ]?.[field.name] ||
                                errors[
                                field.name
                                ]
                            }
                            mode="date"
                        />
                    );
                })}
            </div>

            <div className="row g-2 mt-3">
                {qtyFields.map((field) => {
                    const rate =
                        getRate(
                            room,
                            field.name
                        );

                    const labelText =
                        field.name ===
                            "extra_child"
                            ? `Extra Child Age ${ChildrenMinAge}-${ChildrenMaxAge}`
                            : field.label?.trim();

                    const rateText =
                        rate > 0
                            ? ` · ₹${rate.toLocaleString()}/night`
                            : "";

                    return (
                        <FormInput
                            key={field.name}
                            field={{
                                ...field,
                                label:
                                    labelText +
                                    rateText,
                                min: 0,
                                disabled:
                                    !room.extra_Capablity,
                            }}
                            value={
                                room[field.name]
                            }
                            handleChange={onRoomFieldEvent(
                                index
                            )}
                            error={
                                errors?.rooms?.[
                                index
                                ]?.[field.name] ||
                                ([
                                    "extra_person",
                                    "extra_bed",
                                ].includes(
                                    field.name
                                ) &&
                                    Number(
                                        room[
                                        field.name
                                        ]
                                    ) >
                                    exbedCapabilityLimit
                                    ? `${field.label} maximum ${exbedCapabilityLimit}`
                                    : "")
                            }
                        />
                    );
                })}
            </div>

            <div
                className="d-flex align-items-center justify-content-between mt-3 pt-3"
                style={{ borderTop: "0.5px solid var(--bs-border-color)" }}
            >
                <span
                    style={{
                        fontSize: 12,
                        color: hasQty
                            ? "var(--bs-body-color)"
                            : "var(--bs-secondary-color)",
                    }}
                >
                    {hasQty
                        ? `${totalItems} item${totalItems !== 1 ? "s" : ""} · ₹${estimatedTotal.toLocaleString()} estimated`
                        : "Enter quantities to preview charges"}
                </span>

                <button
                    type="button"
                    className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                    disabled={!hasQty}
                    onClick={() => setShowBill(true)}
                >
                    <i className="ti ti-receipt" style={{ fontSize: 14 }} />
                    Show Bill
                </button>
            </div>

            <Modal
                show={showBill}
                onHide={() => setShowBill(false)}
                centered
                size="md"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: 16 }}>
                        <i className="ti ti-receipt" style={{ fontSize: 18, color: "#185FA5" }} />
                        Charge Breakdown
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <ExtraChargeSummary
                        room={room}
                        values={values}
                        extraFields={extraFields}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowBill(false)}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </td>
    );
};

export default ExtraFacilitySection;